// ═══════════════════════════════════════════════════════════════════════════
// Living Engine Tooltip — portaled, auto-flipping hover tooltip
// packages/@forgetrack/engine/src/Tooltip.tsx
// ═══════════════════════════════════════════════════════════════════════════

import {
  useState, useRef, useCallback, useEffect, useLayoutEffect,
  type ReactNode, type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from './tokens';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  placement?: Placement;
  maxWidth?: number;
  delay?: number;
  style?: CSSProperties;
  disabled?: boolean;
}

export function Tooltip({
  children, content,
  placement = 'bottom',
  maxWidth = 320,
  delay = 250,
  style,
  disabled = false,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; pos: Placement }>({
    top: 0, left: 0, pos: placement,
  });
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const showTimer = useRef<number | null>(null);

  const clearTimer = () => {
    if (showTimer.current != null) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  };

  const show = useCallback(() => {
    if (disabled) return;
    clearTimer();
    showTimer.current = window.setTimeout(() => setVisible(true), delay);
  }, [disabled, delay]);

  const hide = useCallback(() => {
    clearTimer();
    setVisible(false);
  }, []);

  useEffect(() => () => clearTimer(), []);

  useLayoutEffect(() => {
    if (!visible) return;
    const trigger = wrapRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 8;

    let pos: Placement = placement;
    let top: number;
    let left: number;

    if (pos === 'bottom') {
      top = triggerRect.bottom + gap;
      if (top + tipRect.height > vh - 8) { pos = 'top'; top = triggerRect.top - gap - tipRect.height; }
    } else if (pos === 'top') {
      top = triggerRect.top - gap - tipRect.height;
      if (top < 8) { pos = 'bottom'; top = triggerRect.bottom + gap; }
    } else if (pos === 'right') {
      top = triggerRect.top + triggerRect.height / 2 - tipRect.height / 2;
    } else {
      top = triggerRect.top + triggerRect.height / 2 - tipRect.height / 2;
    }

    if (pos === 'top' || pos === 'bottom') {
      left = triggerRect.left + triggerRect.width / 2 - tipRect.width / 2;
    } else if (pos === 'right') {
      left = triggerRect.right + gap;
      if (left + tipRect.width > vw - 8) { pos = 'left'; left = triggerRect.left - gap - tipRect.width; }
    } else {
      left = triggerRect.left - gap - tipRect.width;
      if (left < 8) { pos = 'right'; left = triggerRect.right + gap; }
    }

    if (left < 8) left = 8;
    if (left + tipRect.width > vw - 8) left = vw - tipRect.width - 8;
    if (top < 8) top = 8;
    if (top + tipRect.height > vh - 8) top = vh - tipRect.height - 8;

    setCoords({ top, left, pos });
  }, [visible, placement]);

  return (
    <span
      ref={wrapRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={{ display: 'inline-flex', ...style }}
    >
      {children}
      {visible && !disabled && createPortal(
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top, left: coords.left,
            maxWidth, zIndex: 10050,
            padding: '10px 12px',
            fontSize: 11, lineHeight: 1.55, fontFamily: F.display,
            color: C.textSoft,
            background: 'rgba(6,12,24,0.97)',
            border: `1px solid ${C.faint}`,
            borderRadius: 8,
            boxShadow: '0 12px 30px rgba(0,0,0,0.55)',
            backdropFilter: 'blur(12px)',
            pointerEvents: 'none',
            animation: 'engine-tooltip-in 0.18s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </span>
  );
}

export function TooltipTitle({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 700, fontFamily: F.display,
      color: C.text, marginBottom: 6, letterSpacing: '-0.01em',
    }}>
      {children}
    </div>
  );
}

export function TooltipBody({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 11, lineHeight: 1.55, color: C.textSoft }}>
      {children}
    </div>
  );
}

export function TooltipHint({
  kind, children,
}: {
  kind: 'reversible' | 'irreversible' | 'cost' | 'note';
  children: ReactNode;
}) {
  const color =
    kind === 'reversible' ? C.neon :
    kind === 'irreversible' ? C.coral :
    kind === 'cost' ? C.amber :
    C.cyan;
  const label =
    kind === 'reversible' ? 'Reversible' :
    kind === 'irreversible' ? 'Not reversible' :
    kind === 'cost' ? 'Costs money' :
    'Note';
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      marginTop: 8, padding: '3px 8px',
      fontSize: 10, fontFamily: F.mono, fontWeight: 600,
      letterSpacing: '0.04em', color,
      background: `${color}15`,
      border: `1px solid ${color}40`,
      borderRadius: 4,
    }}>
      <span style={{ textTransform: 'uppercase' }}>{label}:</span>
      <span style={{ color: C.textSoft, fontFamily: F.display, fontWeight: 400, textTransform: 'none' }}>
        {children}
      </span>
    </div>
  );
}

export default Tooltip;
